/**
 * Fase 7 (offline-first + fotos): comprime a foto no dispositivo ANTES de gravar no IndexedDB —
 * importa tanto pro espaço da fila offline (fotos em bytes crus se acumulam rápido numa vistoria
 * de 24 seções) quanto pro tamanho do upload em sinal ruim (casa de bombas/subsolo/elevador, o
 * cenário que justifica a Fase 6 inteira). Canvas-based porque roda em qualquer navegador,
 * inclusive Safari/iOS — sem depender de `createImageBitmap` com opções de resize, que tem
 * suporte inconsistente nesse alvo.
 *
 * O servidor ainda gera suas próprias 3 variantes via sharp (armazenamentoFotos.ts) a partir do
 * que chega aqui — a compressão do device e a do servidor são independentes por design: o device
 * garante um upload pequeno mesmo com sinal ruim, o servidor garante variantes consistentes
 * (miniatura/pdfEmbed) não importa o que o device mandou.
 */
const MAIOR_LADO_MAX = 1800;
const QUALIDADE_JPEG = 0.75;

export async function comprimirImagem(arquivo: File): Promise<Blob> {
  const bitmap = await criarBitmap(arquivo);
  try {
    const escala = Math.min(1, MAIOR_LADO_MAX / Math.max(bitmap.width, bitmap.height));
    const largura = Math.round(bitmap.width * escala);
    const altura = Math.round(bitmap.height * escala);

    const canvas = document.createElement("canvas");
    canvas.width = largura;
    canvas.height = altura;
    const ctx = canvas.getContext("2d");
    if (!ctx) return arquivo; // sem contexto 2d disponível (raríssimo) — segue com o original

    ctx.drawImage(bitmap, 0, 0, largura, altura);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", QUALIDADE_JPEG)
    );
    // toBlob pode devolver null em navegadores/condições raras (canvas "tainted", memória) — cai
    // pro arquivo original em vez de perder a foto.
    return blob ?? arquivo;
  } finally {
    bitmap.close();
  }
}

async function criarBitmap(arquivo: File): Promise<ImageBitmap> {
  // createImageBitmap já aplica a orientação EXIF corretamente na maioria dos navegadores atuais
  // (incluindo Safari iOS recente); sem isso, fotos tiradas na vertical apareceriam giradas.
  return createImageBitmap(arquivo, { imageOrientation: "from-image" });
}
